import { hostModes, routes as extensionRoutes } from "@afriannotate/extensions";

/**
 * The extension host mode active for this page load, if any. A host mode
 * lets the same bundle behave as a different product on another host
 * (own brand, clean URLs, anonymous access), driven by APP_SETTINGS.
 */
export const activeHostMode = (() => {
  try {
    const settings = (typeof window !== "undefined" && window.APP_SETTINGS) || {};
    return (hostModes ?? []).find((m) => typeof m?.isActive === "function" && m.isActive(settings)) ?? null;
  } catch {
    return null;
  }
})();

const chromelessPrefixes = [
  ...(activeHostMode?.chromelessPrefixes ?? []).filter(Boolean),
  ...(extensionRoutes ?? []).filter((r) => r?.chromeless && typeof r.path === "string").map((r) => r.path),
];

/** True when the platform header and sidebar must stay hidden on this path. */
export const isChromelessPath = (pathname) => {
  const p = pathname || "/";
  return chromelessPrefixes.some((prefix) => p === prefix || p.startsWith(`${prefix}/`));
};

/**
 * Wrap a history object so the router sees mapped paths while the browser
 * shows the host mode's clean ones (both directions supplied by the mode).
 */
export const wrapHistoryWithMapping = (h, mapping) => {
  const { toRouterPath, toBrowserPath } = mapping;
  const mapLoc = (to, fn) => (typeof to === "string" ? fn(to) : { ...to, pathname: fn(to.pathname || "/") });
  const routerLocation = (loc) => ({ ...loc, pathname: toRouterPath(loc.pathname) });
  return {
    get length() {
      return h.length;
    },
    get action() {
      return h.action;
    },
    get location() {
      return routerLocation(h.location);
    },
    get isBlocking() {
      return h.isBlocking;
    },
    set isBlocking(v) {
      h.isBlocking = v;
    },
    createHref(loc) {
      return h.createHref(mapLoc(loc, toBrowserPath));
    },
    push(to, state) {
      h.push(mapLoc(to, toBrowserPath), state);
    },
    replace(to, state) {
      h.replace(mapLoc(to, toBrowserPath), state);
    },
    go(n) {
      h.go(n);
    },
    goBack() {
      h.goBack();
    },
    goForward() {
      h.goForward();
    },
    block(...args) {
      return h.block(...args);
    },
    listen(cb) {
      return h.listen((loc, action) => cb(routerLocation(loc), action));
    },
  };
};
